import { ActionBulkModalAiLookalike } from '@aibees/elephant/components/action-bulk-modal-ai-lookalike';
import { AssignOwner } from '@aibees/elephant/components/assign-owner';
import { CreateActionBulkDialog } from '@aibees/elephant/components/create-action-bulk-dialog';
import { ListLabelActionBulkModal } from '@aibees/elephant/components/list-label-action-bulk-modal';
import { useLabelDialogConfig } from '@aibees/elephant/hooks/labels/use-label-dialog-config';
import { LabelType } from '@aibees/elephant/types/label';
import {
  DialogActions,
  DialogTitle,
  Form,
  FormOnSubmit,
} from '@aibees/shared/components';
import { useBoolean } from '@aibees/shared/hooks';
import { Dialog, DialogContent, Fade } from '@mui/material';
import { memo, useCallback } from 'react';
import { config, scheme } from './config';
import * as SX from './sx';
import type { AddToListDialogProps as Props } from './types';
import { useAddListDialog } from './use-add-list-dialog';

const stopPropagation = (e: React.MouseEvent<HTMLDivElement>) =>
  e.stopPropagation();
const DIALOG_PAPER_PROPS = { onMouseDown: stopPropagation };

let { defaultValues } = config;

export const SearchTableActionAddListDialog = memo(({ className }: Props) => {
  // Custom hook for add list dialog logic
  const {
    listDialogState,
    ids,
    lists,
    expandedAccordion,
    showAiLookalike,
    workspace,
    handleAddToList,
    handleClose,
    handleDialogClose,
    setExpandedAccordion,
    grouping,
  } = useAddListDialog();

  // Local state
  const loading = useBoolean(false);
  const state = useBoolean(false);
  const { titleDialog, inputTitle, placeholder } = useLabelDialogConfig({
    isLabel: false,
  });

  // Memoize the handleSubmit callback
  const handleSubmit: FormOnSubmit = useCallback(
    (data) => {
      handleAddToList(data, {
        startProgress: loading.onTrue,
        endProgress: loading.onFalse,
        toggle: () => handleClose('list'),
      });
    },
    [handleAddToList, loading.onTrue, loading.onFalse, handleClose]
  );

  defaultValues ||= {};

  return (
    <Form
      id="add-list-dialog"
      scheme={scheme}
      loading={loading.value}
      customSubmitButton
      defaultValues={defaultValues}
      sx={SX.form}
      onSubmit={handleSubmit}
    >
      <Dialog
        open={listDialogState.value}
        fullWidth={true}
        maxWidth="xs"
        TransitionComponent={Fade}
        onClose={handleDialogClose}
        PaperProps={DIALOG_PAPER_PROPS}
      >
        <DialogTitle
          title="Add to List"
          icon="playlist-add-outline"
          onClose={handleDialogClose}
        />

        <DialogContent padding="none">
          <ListLabelActionBulkModal
            type={LabelType.LIST}
            state={state}
            firstState={listDialogState}
            optional={false}
            expandedAccordion={expandedAccordion}
            setExpandedAccordion={setExpandedAccordion}
          />

          {workspace && (
            <AssignOwner
              expandedAccordion={expandedAccordion}
              setExpandedAccordion={setExpandedAccordion}
            />
          )}

          <ActionBulkModalAiLookalike
            expandedAccordion={expandedAccordion}
            setExpandedAccordion={setExpandedAccordion}
          />
        </DialogContent>

        <DialogActions
          formId="add-list-dialog"
          disabled={!lists.length}
          onClose={handleDialogClose}
        />
      </Dialog>

      <CreateActionBulkDialog
        state={state}
        firstState={listDialogState}
        type={LabelType.LIST}
        title={titleDialog}
        inputTitle={inputTitle}
        placeholder={placeholder}
        grouping={grouping}
      />
    </Form>
  );
});

SearchTableActionAddListDialog.displayName = 'SearchTableActionAddListDialog';
